/**
 * Where saves live.
 *
 * The interface is deliberately tiny and byte-oriented: a save is an
 * ArrayBuffer with a name. That is the whole contract, and it is what makes
 * the storage backend swappable without touching game code.
 *
 *   IndexedDbStore   in the player's browser        - what ships today
 *   HttpSaveStore    POST/GET against an API        - when there are accounts
 *   FileSaveStore    a downloaded / uploaded file   - export and import
 *
 * All three implement `SaveStore`; main.ts never learns which it has.
 */

export interface SaveSlotInfo {
  name: string;
  savedAt: number;
  bytes: number;
}

export interface SaveStore {
  list(): Promise<SaveSlotInfo[]>;
  read(name: string): Promise<ArrayBuffer | null>;
  write(name: string, data: ArrayBuffer): Promise<void>;
  remove(name: string): Promise<void>;
}

const DB_NAME = "vyzhyvannia";
const DB_VERSION = 1;
const STORE = "saves";

interface StoredRow {
  name: string;
  savedAt: number;
  data: ArrayBuffer;
}

function openAt(version?: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = version === undefined
      ? indexedDB.open(DB_NAME)
      : indexedDB.open(DB_NAME, version);
    request.onupgradeneeded = (): void => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "name" });
      }
    };
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error ?? new Error("indexedDB failed"));
  });
}

/**
 * Open the database, and make sure it actually has the store in it.
 *
 * **Why the second attempt exists.** A database of this name can already be
 * there without the store - anything that opened it at this version without
 * an upgrade handler leaves exactly that, and then every read throws
 * NotFoundError for as long as that browser profile lives. It happened here:
 * a test opened the database before the game did, and afterwards the save
 * panel would not draw at all.
 *
 * Opening with no version asks for whatever is there, so a browser that is
 * already ahead of us is not knocked backwards; if the store is still missing
 * we reopen one version higher, which is the only way to be handed an upgrade
 * transaction to create it in.
 */
function openDatabase(): Promise<IDBDatabase> {
  return openAt().then(async (db) => {
    if (db.objectStoreNames.contains(STORE)) return db;
    const version = db.version + 1;
    db.close();
    return openAt(Math.max(version, DB_VERSION));
  });
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error ?? new Error("request failed"));
  });
}

/**
 * Saves in the player's own browser.
 *
 * IndexedDB rather than localStorage: it stores ArrayBuffers directly instead
 * of forcing everything through base64 strings, and it has room for a real
 * world rather than a few megabytes of text.
 *
 * This is per-browser and per-device. Clearing site data deletes it, and it
 * does not follow the player to another machine. See the README for what
 * changes when the game is hosted publicly.
 */
export class IndexedDbStore implements SaveStore {
  private db: Promise<IDBDatabase> | null = null;

  private connect(): Promise<IDBDatabase> {
    this.db ??= openDatabase();
    return this.db;
  }

  /** True when this browser will let us store anything at all. */
  static isAvailable(): boolean {
    try {
      return typeof indexedDB !== "undefined";
    } catch {
      return false;
    }
  }

  async list(): Promise<SaveSlotInfo[]> {
    const db = await this.connect();
    const tx = db.transaction(STORE, "readonly");
    const rows = await promisify(tx.objectStore(STORE).getAll() as IDBRequest<StoredRow[]>);
    return rows
      .map((r) => ({ name: r.name, savedAt: r.savedAt, bytes: r.data.byteLength }))
      .sort((a, b) => b.savedAt - a.savedAt);
  }

  async read(name: string): Promise<ArrayBuffer | null> {
    const db = await this.connect();
    const tx = db.transaction(STORE, "readonly");
    const row = await promisify(
      tx.objectStore(STORE).get(name) as IDBRequest<StoredRow | undefined>,
    );
    return row?.data ?? null;
  }

  async write(name: string, data: ArrayBuffer): Promise<void> {
    const db = await this.connect();
    const tx = db.transaction(STORE, "readwrite");
    const row: StoredRow = { name, savedAt: Date.now(), data };
    await promisify(tx.objectStore(STORE).put(row));
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = (): void => resolve();
      tx.onerror = (): void => reject(tx.error ?? new Error("write failed"));
    });
  }

  async remove(name: string): Promise<void> {
    const db = await this.connect();
    const tx = db.transaction(STORE, "readwrite");
    await promisify(tx.objectStore(STORE).delete(name));
  }
}
